<template>
  <fieldset
    :class="[
      $blockClass(blockClass, 'swatches')
    ]"
  >
    <legend
      class="visually-hidden"
      v-text="$string('accessibility.product.swatch_label')"
    />
    <ul
      v-if="render"
      class="product-swatches"
    >
      <li
        v-for="(object) of filteredArray"
        :key="`object-${object?.handle}-${object?.swatch?.handle}-${cardIndex}`"
        class="product-swatch"
        :class="[
          $blockClass(blockClass, 'swatch')
        ]"
      >
        <input
          :id="getId(object)"
          v-model="localModelValue"
          class="product-swatch__input visually-hidden"
          :class="{ 'product-swatch__input--unavailable': !isAvailable(object) }"
          autocomplete="off"
          :name="inputName"
          type="radio"
          :value="object?.swatch?.handle"
          @input="handleInput(object)"
          @focusin="handleMouseEnter(object?.swatch?.name, optionName)"
          @focusout="handleMouseLeave"
        >

        <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions -->
        <label
          class="product-swatch__label"
          :for="getId(object)"
          :style="`background-image: url(${object?.swatch?.src})`"
          @focusin="handleMouseEnter(object?.swatch?.name, optionName)"
          @focusout="handleMouseLeave"
          @mouseenter="handleMouseEnter(object?.swatch?.name, optionName)"
          @mouseleave="handleMouseLeave"
        >
          <span
            class="visually-hidden"
            v-text="object?.swatch?.name"
          />
        </label>
      </li>
      <li
        v-if="swatchLink && productLink.length"
        class="product-swatch__link"
      >
        <a
          :href="productLink"
          class="product-swatches__link text-body-2xs-regular"
          v-text="`+${swatchDifference}`"
        />
      </li>
    </ul>

    <span
      v-if="productForm"
      class="product-swatches__active-name text-body-2xs-regular"
      v-text="label"
    />
  </fieldset>
</template>

<script>

/**
 * Vue: Product swatches (product-swatches)
 * -----------------------------------------------------------------------------
 * Product color swatches.
 *
 * @param {String} [activeExtensionName] - Active extension name.
 * @param {Object} activeGroup - Active group object.
 * @param {String} [blockClass] - Block level class to apply to all elements.
 * @param {Number} [cardIndex] - Index of parent product card.
 * @param {String} [label] - Label passed from product form.
 * @param {Array} [options] - Product options used to determine if legacy swatch
 * is unavailable.
 * @param {Boolean} [productForm] - Whether this is in a product form.
 * @param {Boolean|Object} product - Current product.
 * @param {String} optionName - Swatch Name.
 */
import { mapState } from 'vuex'

export default {
  name: 'ProductSwatches',

  props: {
    productLink: {
      type: String,
      default: '',
    },
    cardLayout: {
      type: String,
      default: 'regular',
    },
    activeExtensionName: {
      type: String,
      default: '',
    },
    activeGroup: {
      type: Object,
      default: () => ({}),
      required: true,
    },
    blockClass: {
      type: String,
      default: '',
    },
    cardIndex: {
      type: Number,
      default: null,
    },
    label: {
      type: String,
      default: '',
    },
    options: {
      type: Array,
      default: () => [],
    },
    optionName: {
      type: String,
      default: '',
    },
    productForm: {
      type: Boolean,
      default: false,
    },
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    isDickeyBundle: {
      type: Boolean,
      default: false,
    },
  },

  emits: {
    'update-group': (payload) => typeof payload === 'object',
    'update-product-handle': (payload) => typeof payload === 'string',
    'update-product-id': (payload) => ['number', 'string'].includes(typeof payload),
    'swatch-mouse-enter': (payload) => typeof payload === 'object',
    'swatch-mouse-leave': () => true,
  },

  data() {
    return {
      cnvsGlobal: true,
      initialHandle: this.product?.handle,
      localModelValue: '',
      swatchDifference: 0,
      swatchLimitDesktop: this.$variable('settings.swatchLimit.desktop'),
      swatchLimitDesktopLarge: this.$variable('settings.swatchLimit.desktopLarge'),
      swatchLimitRegular: this.$variable('settings.swatchLimit.regular'),
      swatchLimitSmall: this.$variable('settings.swatchLimit.small'),
      swatchLink: false,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute extension grouping products.
     * - Enables rendering all swatches even if they do not match current
     *   extension name.
     * - Only output in product form to avoid query overhead on product cards.
     * @returns {Array}
     */
    extensionGrouping() {
      if (
        !this.product?.extensions?.length ||
        !this.activeExtensionName ||
        !this.productForm
      ) {
        return []
      }

      /**
       * Flatten all extension groups.
       * - Filters out groups which shouldn't be rendered.
       */
      const allGroups = this.product?.extensions
        .map((extension) => extension.grouping)
        .flat()
        .filter((group) => group?.render)

      /**
       * Build list of unique group names.
       */
      const uniqueGroupNames = []

      allGroups.forEach((group) => {
        if (uniqueGroupNames.includes(group.swatch?.name)) {
          return
        }

        uniqueGroupNames.push(group.swatch?.name)
      })

      /**
       * Go through each unique group handle and build array.
       * - Try to find group that matches current extension.
       * - Otherwise use first group that matches.
       */
      return uniqueGroupNames
        .map((name) => {
          const extensionMatchGroup = allGroups.find((group) => {
            return group.swatch?.name === name && group.extension === this.activeExtensionName
          })

          if (extensionMatchGroup) {
            return extensionMatchGroup
          }

          const nameMatchGroup = allGroups.find((group) => {
            return group.swatch?.name === name
          })

          if (nameMatchGroup) {
            return nameMatchGroup
          }

          return false
        })
        .filter(Boolean)
    },

    /**
     * Compute array used for swatches.
     * - Filters swatches which shouldn't be rendered.
     * @returns {Array}
     */
    filteredArray() {
      if (this.extensionGrouping?.length) {
        return this.reduceSwatches(this.extensionGrouping)
      }

      if (this.product?.grouping?.length) {
        return this.reduceSwatches(this.product?.grouping.filter((group) => group.render))
      }

      if (this.product?.legacySwatches?.length) {
        return this.reduceSwatches(this.product?.legacySwatches.filter(this.isRendered))
      }

      if (this.isDickeyBundle && this.product?.currentSwatch[0]?.swatch?.name) {
        return this.product?.currentSwatch
      }

      return []
    },

    /**
     * Compute input name.
     * - Name attribute should be the same between all inputs but unique per
     *   product when on the PLP.
     * @returns {String}
     */
    inputName() {
      return this.productForm
        ? `swatch-for-${this.product.handle}`
        : `swatch-for-${this.initialHandle}`
    },

    /**
     * Compute whether or not to render product groups.
     * @returns {Boolean}
     */
    render() {
      return Boolean(this.filteredArray.length || this.productForm)
    },
  },

  watch: {

    /**
     * Watch active group to update local v-model handle.
     */
    activeGroup: {
      handler() {
        if (!this.activeGroup?.swatch?.handle) {
          return
        }

        this.localModelValue = this.activeGroup?.swatch?.handle
      },
      immediate: true,
    },
  },

  methods: {

    /**
     * Get input name attribute.
     * @param {String} objectProductHandle - Object product handle.
     * @returns {String}
     */
    getInputName(objectProductHandle) {
      return this.productForm
        ? `swatch-${objectProductHandle}`
        : `${this.initialHandle}-swatch-${objectProductHandle}`
    },

    /**
     * Compute id to use for this swatch input.
     * @param {Object} object - Group/swatch.
     * @returns {String}
     */
    getId(object) {
      return this.productForm
        ? `${object?.handle}-${object?.swatch?.handle}-product-form`
        : `${this.initialHandle}-${object?.handle}-${object?.swatch?.handle}`
    },

    /**
     * Get if the object should be rendered.
     * @param {Object} object - Group/swatch.
     * @returns {Boolean}
     */
    isRendered(object) {
      if (object.type === 'legacy' && this.productForm) {
        return this.isAvailable(object) || !object.finalSale
      }

      return object.render
    },

    /**
     * Get if the object is available.
     * @param {Object} object - Group/swatch.
     * @returns {Boolean}
     */
    isAvailable(object) {
      if (object.type !== 'legacy') {
        return object.available
      }

      if (this.productForm) {
        return !this.options[0]?.disabled[object.swatch.name]
      }

      return this.product?.variants
        .filter((variant) => variant.selectedOptions[0]?.value === object.swatch.name)
        .some((variant) => variant.available)
    },

    /**
     * Handle group input change
     * @param {Object} object - Group/swatch.
     */
    handleInput(object) {
      if (object?.handle && object?.handle !== this.product?.handle) {
        if (this.productForm) {
          this.$emit('update-product-handle', object.handle)
        } else {
          this.$emit('update-product-id', object.id)
        }
      }

      this.$emit('update-group', object)
    },

    /**
     * Check if group is active.
     * @param {Object} object - Group/swatch.
     * @returns {Boolean}
     */
    isActive(object) {
      if (this.activeGroup && object === this.activeGroup) {
        return true
      }

      if (object?.swatch?.name?.toLowerCase() === this.activeGroupName?.toLowerCase()) {
        return true
      }

      return false
    },

    /**
     * Reduce swatches to limit.
     * @param {Array} swatches - Swatches to reduce.
     * @returns {Array}
     */
    reduceSwatches(swatches) {
      if (this.productForm) {
        return swatches
      }

      let swatchesMaxLength = swatches.length

      if (this.isMobile) {
        if (this.cardLayout === 'regular') {
          swatchesMaxLength = this.swatchLimitRegular
        } else {
          swatchesMaxLength = this.swatchLimitSmall
        }
      } else if (this.cardLayout === 'large') {
        swatchesMaxLength = this.swatchLimitDesktopLarge
      } else {
        swatchesMaxLength = this.swatchLimitDesktop
      }

      if (swatchesMaxLength < swatches.length) {
        this.swatchLink = true
        this.swatchDifference = swatches.length - swatchesMaxLength
      } else {
        this.swatchLink = false
        this.swatchDifference = 0
      }

      return [...swatches.slice(0, swatchesMaxLength)]
    },

    /**
     * @param {String} swatchName swatch name
     * @param {String} swatchHandle swatch handle
     */
    handleMouseEnter(swatchName, swatchHandle) {
      this.$emit(
        'swatch-mouse-enter',
        {
          swatchName,
          swatchHandle,
        },
      )
    },

    /**
     * Handle mouse leave event.
     */
    handleMouseLeave() {
      this.$emit('swatch-mouse-leave')
    },
  },
}
</script>
